import React, { useCallback, useRef, useState, ReactElement } from 'react';
import axios from 'axios';
import useScript from 'hooks/useScript';
import { useEffect } from 'react';
import {
  addItemToLocalStorage,
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
} from 'lib/util';

export const DITTO_SIG_KEY = 'ditto_sig';
export const DITTO_SCANID_KEY = 'ditto_scanid';
const DITTO_EXPIRE_TIME = 1000 * 60 * 30;

type Vto = {
  destroy: () => void;
};

declare const Ditto: {
  Overlay: new (opts: unknown, callbacks: unknown) => Vto;
  Scan: new (opts: unknown, callbacks: unknown) => Vto;
};

type VtoProps = {
  glassesId: string;
  forceScan: boolean;
  disableClose?: boolean;
  onStep?: (step?: VtoSteps) => void;
  onClose?: () => void;
};

export type VtoSteps = 'init' | 'scan' | 'overlay' | 'rescan';

export const clearVtoScanData = async (): Promise<void> => {
  const scanId = JSON.parse(getItemFromLocalStorage(DITTO_SCANID_KEY) || 'null');
  if (!scanId) return;
  await axios.get(`${window.location.origin}/api/vto-clear`, {
    headers: {
      'x-scan-id': scanId,
    },
  });
  removeItemFromLocalStorage(DITTO_SCANID_KEY);
};

/**
 * Fetches stored signature and if exists checks if the timestamp has not expired.
 */
const getStoredSignature = (): string | false => {
  const sig = JSON.parse(getItemFromLocalStorage(DITTO_SIG_KEY) || 'null');
  if (!sig) return false;
  if (Date.now() > sig.timestamp + DITTO_EXPIRE_TIME) {
    removeItemFromLocalStorage(DITTO_SIG_KEY);
    return false;
  }
  return sig.signature;
};

/**
 * Gets a newly generated signature from the API.
 */
const getGeneratedSignature = async (message?: string): Promise<string> => {
  const response = await axios.get(
    `${window.location.origin}/api/vto-signature${message ? `?message=${message}` : ''}`
  );
  if (response.status === 200 && response.data.signature !== '') {
    return response.data.signature;
  }
  throw new Error('Unable to retrieve signature');
};

export default function VTO({
  glassesId,
  forceScan,
  disableClose = false,
  onStep = () => {
    return;
  },
  onClose = () => {
    return;
  },
}: VtoProps): ReactElement {
  const [loaded, setLoaded] = useState(false);
  const [signature, setSignature] = useState('');
  const [step, setStep] = useState<VtoSteps>('init');
  const [error, setError] = useState('');
  const scanId = useRef('');
  const overlaySignature = useRef('');
  useScript('https://legacy-bsdk.fittingbox.com/default/5.8.9/en-us/api.js', false, () =>
    setLoaded(true)
  );

  const getSignature = async (): Promise<void> => {
    const storedSig = getStoredSignature();
    if (storedSig) {
      setSignature(storedSig);
    } else {
      try {
        const signature = await getGeneratedSignature();
        addItemToLocalStorage(
          DITTO_SIG_KEY,
          JSON.stringify({
            signature: signature,
            timestamp: Date.now(),
          })
        );
        setSignature(signature);
      } catch (e) {
        setError('Unable to start scan at this time.');
        console.error(e);
      }
    }
  };
  useEffect(() => {
    getSignature();
  }, []);

  const vtoOpts = useCallback(
    () => ({
      tryOnServer: process.env.NEXT_PUBLIC_DITTO_VTO_SERVER,
      partnerId: process.env.NEXT_PUBLIC_DITTO_PARTNER_ID,
      accessKey: process.env.NEXT_PUBLIC_DITTO_ACCESS_KEY,
      glassesId: glassesId,
    }),
    [glassesId]
  );

  /**
   * Display overlay
   */
  const runOverlay = useCallback((): void => {
    const overlay = new Ditto.Overlay(
      {
        ...vtoOpts(),
        domSelector: '#vtoOverlay',
        scanId: scanId.current,
        overlaySignature: overlaySignature.current,
        disablePreview: false,
      },
      {
        failure: () => {
          // go back to scan step, force rescan
          overlay.destroy();
          setStep('scan');
        },
      }
    );
  }, [vtoOpts]);

  /**
   * Initiate face scan.
   */
  const runScan = useCallback((): void => {
    const scan = new Ditto.Scan(
      {
        ...vtoOpts(),
        domSelector: '#vtoScan',
        partnerSignature: signature,
        faceInsightsServer: process.env.NEXT_PUBLIC_DITTO_FACEIQ_SERVER,
        enableFaceInsights: true,
        disableClose: disableClose,
        setScale: 'optional',
      },
      {
        success: (response: { scanId: string; overlaySignature: string }) => {
          overlaySignature.current = response.overlaySignature;
          scanId.current = response.scanId;
          addItemToLocalStorage(DITTO_SCANID_KEY, JSON.stringify(scanId.current));
          scan.destroy();
          setStep('overlay');
        },
        failure: (response: { status: string }) => {
          // handle SCAN_FAILED
          if (response.status === 'SCAN_FAILED') {
            scan.destroy();
            setError('Scan failed, refresh the page to try again.');
          }
          // handle CAMERA_ACCESS_DENIED
          if (response.status === 'CAMERA_ACCESS_DENIED') {
            scan.destroy();
            setError('Camera access is required for Virtual Try-On.');
          }
        },
        close: () => {
          onClose();
        },
      }
    );
  }, [onClose, signature, vtoOpts, disableClose]);

  /**
   * Kickoff scan or overlay once sdk is loaded and signature is set.
   */
  useEffect(() => {
    const kickoff = async (): Promise<void> => {
      if (!loaded || !signature) return;
      const storedScanId = JSON.parse(getItemFromLocalStorage(DITTO_SCANID_KEY) || 'null');
      if (!storedScanId || forceScan) {
        setStep('scan');
        return;
      }
      try {
        overlaySignature.current = await getGeneratedSignature(storedScanId);
        scanId.current = storedScanId;
        setStep('overlay');
      } catch (e) {
        setStep('scan');
      }
    };
    kickoff();
  }, [loaded, signature, forceScan]);

  useEffect(() => {
    if (step === 'scan') runScan();
    else if (step === 'overlay') runOverlay();
    onStep(step);
  }, [step, runScan, runOverlay, onStep]);

  const rescan = (): void => setStep('rescan');

  return (
    <div className="w-full text-center">
      {error && <div className="text-lg font-bold">{error}</div>}
      {step === 'overlay' && (
        <>
          <div id="vtoOverlay" className="h-screen overflow-hidden md:h-vto-desktop-overlay"></div>
          <button className="text-tertiary-actual py-2" onClick={rescan}>
            Not you? Click here to scan.
          </button>
        </>
      )}
      {step === 'scan' && <div id="vtoScan" className="flex items-center md:h-auto"></div>}
    </div>
  );
}
